import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Star, Smile, Users, Eye, Flag, Globe } from "lucide-react";

export default function Philosophy() {
  // Motion config (same as OurStory)
  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.3 },
    },
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
  };

  const coreValues = [
    {
      icon: ShieldCheck,
      title: "Safety as Our Foundation",
      desc: "Every decision, process, and product begins with the commitment to protect our people, customers, and the environment.",
    },
    {
      icon: Star,
      title: "Quality Without Compromise",
      desc: "We uphold the highest global standards, ensuring precision, consistency, and reliability in every batch we produce.",
    },
    {
      icon: Smile,
      title: "Customer Delight",
      desc: "We go beyond satisfaction — delivering trust, responsiveness, and value that strengthen long-term partnerships.",
    },
    {
      icon: Users,
      title: "Ethical Integrity",
      desc: "We operate with honesty, fairness, and transparency, earning respect in every interaction.",
    },
    {
      icon: Globe,
      title: "One Team, One Vision",
      desc: "Together, we combine our strengths, trust, and expertise to achieve excellence beyond expectations.",
    },
  ];

  return (
    <div>
      {/* Hero Banner */}
      <div
        className="relative h-[300px] md:h-[400px] w-full bg-cover bg-center flex items-center justify-center"
        style={{ backgroundImage: "url('/images/ourphilosophy.webp')" }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/30 to-transparent"></div>
        <h1 className="relative z-10 text-3xl sm:text-4xl md:text-5xl font-bold text-white text-center px-4">
          Our Philosophy
        </h1>
      </div>

      {/* Content Section */}
      <motion.section
        id="philosophy"
        className="max-w-6xl mx-auto px-6 py-12 sm:py-16 space-y-16 sm:space-y-20"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={container}
      >
        {/* Intro */}
        <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
          <motion.div
            className="md:w-1/2 rounded-xl overflow-hidden shadow-lg"
            variants={fadeInUp}
          >
            <img
              src="/images/generated-image.webp"
              alt="Illustration representing MAC Labs philosophy of safety and quality"
              loading="lazy"
              className="w-full h-auto object-cover rounded-xl"
            />
          </motion.div>

          <motion.div
            className="md:w-1/2 text-gray-800 space-y-6"
            variants={fadeInUp}
          >
            <p className="text-lg leading-relaxed">
              Our philosophy centers on fostering safety, quality, and integrity in every aspect of our work.
            </p>
            <blockquote className="border-l-4 border-[#0d47a1] pl-6 italic text-[#0d47a1] font-semibold text-xl bg-blue-50 rounded-md p-4 shadow-sm max-w-md">
              "Excellence is not an act, but a habit formed by unwavering dedication across every process and product."
            </blockquote>
            <p className="text-lg leading-relaxed">
              As a team, we strive to cultivate trust and transparency in our partnerships, creating shared success for our customers,
              employees, and community.
            </p>
          </motion.div>
        </div>

        {/* Vision + Mission */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          <motion.div
            className="bg-blue-50 rounded-xl p-8 shadow-md flex flex-col"
            variants={fadeInUp}
          >
            <div className="flex items-center mb-6 text-[#0d47a1]">
              <Eye className="w-8 h-8 mr-4" />
              <h2 className="text-3xl font-semibold">Vision</h2>
            </div>
            <p className="text-gray-700 leading-relaxed text-lg">
              To be a globally admired manufacturer of APIs, Intermediates, Cosmetics, and Specialty Chemicals...
            </p>
          </motion.div>

          <motion.div
            className="bg-blue-50 rounded-xl p-8 shadow-md flex flex-col"
            variants={fadeInUp}
          >
            <div className="flex items-center mb-6 text-[#0d47a1]">
              <Flag className="w-8 h-8 mr-4" />
              <h2 className="text-3xl font-semibold">Mission</h2>
            </div>
            <ul className="list-disc pl-6 text-gray-700 space-y-3 text-lg">
              <li>Ensure safety first...</li>
              <li>Deliver superior quality...</li>
              <li>Continuously improve...</li>
              <li>Build enduring partnerships...</li>
            </ul>
          </motion.div>
        </div>

        {/* Core Values */}
        <motion.div className="space-y-6" variants={fadeInUp}>
          <h2 className="text-3xl font-semibold text-[#0d47a1] text-center">
            Core Values
          </h2>
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto"
            variants={container}
          >
            {coreValues.map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={i}
                className="bg-white p-6 rounded-xl shadow-md border border-gray-200 cursor-pointer"
                variants={fadeInUp}
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 12px 30px rgba(13,71,161,0.25)",
                  borderColor: "#0d47a1",
                  background: "linear-gradient(135deg, #ffffff, #e3f2fd)",
                }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="bg-[#0d47a1] text-white p-3 rounded-full">
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0d47a1]">{title}</h3>
                </div>
                <p className="text-gray-600">{desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </motion.section>
    </div>
  );
}
